import React, { useEffect, useMemo, useState } from "react";
import Button from "components/shared/button";
import Input, { InputType } from "components/shared/inputs/input";
import { Popup } from "components/popups/Popup";
import useTranslation from "utils/hooks/useTranslation";
import useCustomForm from "utils/hooks/useCustomForm";
import { useCreateMenuDefMutation, useUpdateMenuDefMutation } from "store/api";

type MenuDefPopupProps = {
  isOpen: boolean;
  onClose: () => void;
  editingRow?: Record<string, any> | null;
  onSaved?: () => Promise<void> | void;
};

type MenuDefFormValues = {
  name: string;
  icon: string;
  parent: string;
  layoutId: string;
  extUrl: string;
  superAdmin: boolean;
  title: boolean;
};

const MenuDefPopup: React.FC<MenuDefPopupProps> = ({ isOpen, onClose, editingRow, onSaved }) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [createMenuDef] = useCreateMenuDefMutation();
  const [updateMenuDef] = useUpdateMenuDefMutation();

  const isEditMode = useMemo(() => editingRow?.id != null, [editingRow]);

  const { control, getValues, reset } = useCustomForm<MenuDefFormValues>({
    defaultValues: {
      name: "",
      icon: "icon-docs",
      parent: "0",
      layoutId: "0",
      extUrl: "",
      superAdmin: false,
      title: false,
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    if (isEditMode) {
      reset({
        name: String(editingRow?.name ?? ""),
        icon: String(editingRow?.icon ?? "icon-docs"),
        parent: String(editingRow?.parent ?? "0"),
        layoutId: String(editingRow?.layoutId ?? editingRow?.layoutid ?? "0"),
        extUrl: String(editingRow?.extUrl ?? editingRow?.exturl ?? ""),
        superAdmin: Boolean(editingRow?.superAdmin ?? editingRow?.superadmin),
        title: Boolean(editingRow?.title),
      });
      return;
    }

    reset({
      name: "",
      icon: "icon-docs",
      parent: "0",
      layoutId: "0",
      extUrl: "",
      superAdmin: false,
      title: false,
    });
  }, [editingRow, isEditMode, isOpen, reset]);

  const handleClose = () => {
    reset({
      name: "",
      icon: "icon-docs",
      parent: "0",
      layoutId: "0",
      extUrl: "",
      superAdmin: false,
      title: false,
    });
    onClose();
  };

  const handleSave = async () => {
    const values = getValues();
    const name = String(values.name ?? "").trim();
    if (!name) return;

    const parentParsed = Number.parseInt(String(values.parent ?? "0"), 10);
    const layoutParsed = Number.parseInt(String(values.layoutId ?? "0"), 10);

    setIsSaving(true);
    try {
      const payload = {
        name,
        icon: String(values.icon ?? "").trim() || "icon-docs",
        parent: Number.isNaN(parentParsed) ? 0 : parentParsed,
        layoutId: Number.isNaN(layoutParsed) ? 0 : layoutParsed,
        extUrl: String(values.extUrl ?? "").trim(),
        superAdmin: Boolean(values.superAdmin),
        title: Boolean(values.title),
      };

      if (isEditMode) {
        await updateMenuDef({
          id: Number(editingRow?.id),
          data: payload,
        }).unwrap();
      } else {
        await createMenuDef(payload).unwrap();
      }

      handleClose();
      void Promise.resolve(onSaved?.()).catch(() => {
        // refresh failure should not keep popup open after successful create
      });
    } catch {
      // handled by api interceptor
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Popup
      title={isEditMode ? "Modifier menu" : "Nouveau menu"}
      onClose={handleClose}
      portal
      fixSize="middle"
      closeOnOutsideClick
      commandPanelProps={{
        additionalElement: (
          <group data-justify="right" data-gap="10">
            <Button type="button" onClick={handleClose} disabled={isSaving}>
              {t("Back")}
            </Button>
            <Button type="button" primary onClick={handleSave} disabled={isSaving}>
              {isEditMode ? t("save") : t("Add")}
            </Button>
          </group>
        ),
      }}
    >
      <group data-direction="column" data-gap="20" data-space="40" data-space-horizontal="40" data-space-top="5">
        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input
              dataLength="autofit"
              label={"Name"}
              name="name"
              control={control}
              type="text"
              size="large"
            />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input
              dataLength="autofit"
              label={"Icon"}
              name="icon"
              control={control}
              type="text"
              size="large"
            />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input
              dataLength="autofit"
              label={"Parent"}
              name="parent"
              control={control}
              type="number"
              size="large"
              min={0}
            />
          </group>
          <group data-length="forcefit" data-fit="1">
            <Input
              dataLength="autofit"
              label={"Layout ID"}
              name="layoutId"
              control={control}
              type="number"
              size="large"
              min={0}
            />
          </group>
        </group>

        <group data-gap="20">
          <group data-length="forcefit" data-fit="1">
            <Input
              dataLength="autofit"
              label={"External URL"}
              name="extUrl"
              control={control}
              type="text"
              size="large"
            />
          </group>
        </group>

        <group data-gap="20" data-wrap="wrap">
          <group data-width="auto">
            <Input
              id="menu-def-super-admin"
              type={InputType.Checkbox}
              name="superAdmin"
              label={"Super admin"}
              control={control}
            />
          </group>
          <group data-width="auto">
            <Input
              id="menu-def-title"
              type={InputType.Checkbox}
              name="title"
              label={"Title"}
              control={control}
            />
          </group>
        </group>
      </group>
    </Popup>
  );
};

export default MenuDefPopup;
